/**
 * Primitivas de agregação/transformação compartilhadas (doc B clusters
 * 3/5/6/9/11/12). Semântica copiada das implementações canônicas do
 * `pages/operacoes/` (pipeline/derive + Selector + DataTable) pra que as
 * páginas soltas parem de reimplementar.
 */
import { NormalizeSku } from './ml';

/** Dobra a lista num Map<key, item[]> (group-by clássico). Keys nulas/'' são puladas. */
export function GroupBy(list, keyFn) {
    const out = new Map();
    for (const item of list || []) {
        const key = keyFn(item);
        if (key === null || key === undefined || key === '') continue;
        const bucket = out.get(key);
        if (bucket) {
            bucket.push(item);
        } else {
            out.set(key, [item]);
        }
    }
    return out;
}

/**
 * Group-by + redução por campo numa passada:
 *   AggregateBy(items, (i) => i.sku, {
 *       qty: (acc, i) => (acc || 0) + i.qty,   // somar
 *       title: (acc, i) => acc ?? i.title,      // manter-primeiro
 *   }) → Map<sku, {qty, title}>
 * Cada reducer recebe (acumuladoAtual|undefined, item, key).
 */
export function AggregateBy(list, keyFn, reducers) {
    const out = new Map();
    const fields = Object.entries(reducers || {});
    for (const item of list || []) {
        const key = keyFn(item);
        if (key === null || key === undefined || key === '') continue;
        let acc = out.get(key);
        if (!acc) {
            acc = {};
            out.set(key, acc);
        }
        for (const [field, reduce] of fields) {
            acc[field] = reduce(acc[field], item, key);
        }
    }
    return out;
}

/**
 * Indexa a lista por SKU normalizado (`NormalizeSku`) — keep-first em caso de
 * duplicata. `getSku` default lê `item.sku`. Par de `GetBySku` (cluster B-6:
 * mata os `.find(...)` lineares repetidos).
 */
export function IndexBySku(list, getSku = (item) => item?.sku) {
    const out = new Map();
    for (const item of list || []) {
        const key = NormalizeSku(getSku(item));
        if (!key || out.has(key)) continue;
        out.set(key, item);
    }
    return out;
}

/** Lookup num índice de `IndexBySku` aceitando SKU cru (normaliza antes). */
export function GetBySku(indexMap, sku) {
    if (!indexMap) return undefined;
    return indexMap.get(NormalizeSku(sku));
}

/**
 * Crescimento %: (current - past) / past * 100 (cluster B-9).
 * past = 0: current = 0 → 0; senão ±cap (∞ visualizado; default 999, o mesmo
 * cap do `pipeline/derive/PeriodComparison.js`).
 */
export function PctChange(current, past, { cap = 999 } = {}) {
    const c = Number(current || 0);
    const p = Number(past || 0);
    if (p === 0) {
        if (c === 0) return 0;
        return c > 0 ? cap : -cap;
    }
    return ((c - p) / p) * 100;
}

/** Margem % = (price - cost) / price * 100. price inválido/0 → 0 (cluster B-9). */
export function MarginPct(price, cost) {
    const p = Number(price || 0);
    if (!p) return 0;
    return ((p - Number(cost || 0)) / p) * 100;
}

/**
 * Forecast 22 dias = best7×7 + avg7×15, arredondado (cluster B-3 — mesma
 * fórmula do `pipeline/Selector.js` `ComputeForecast`, que era triplicada).
 */
export function ComputeForecast({ best7, avg7 }) {
    return Math.round(Number(best7 || 0) * 7 + Number(avg7 || 0) * 15);
}

/**
 * Extrai a lista de um envelope de API que pode vir como `{results}`,
 * `{<entity>}` ou o próprio array (cluster B-11).
 *   ExtractResults(data)                      → data.results || []
 *   ExtractResults(data, ['results','orders']) → primeiro array encontrado
 */
export function ExtractResults(data, keys = ['results']) {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== 'object') return [];
    for (const key of keys) {
        if (Array.isArray(data[key])) return data[key];
    }
    return [];
}

/**
 * Normaliza `{paging: {total, limit, offset}}` com fallbacks (cluster B-11).
 * `fallbacks` preenche o que faltar (ex.: limit/offset da request atual).
 * `truncated` é aditivo: só GET /orders com `search` o emite (true = o conjunto
 * de candidatos bateu no teto do servidor e `total` é um piso, não o total real);
 * nas demais telas fica `false` e é ignorado.
 */
export function ExtractPaging(data, fallbacks = {}) {
    const paging = data?.paging || {};
    return {
        total: Number(paging.total ?? data?.total ?? fallbacks.total ?? 0),
        limit: Number(paging.limit ?? fallbacks.limit ?? 0),
        offset: Number(paging.offset ?? fallbacks.offset ?? 0),
        truncated: Boolean(paging.truncated),
    };
}

/**
 * Valor de célula (possivelmente formatado: "R$ 12,34", "45%", "30d",
 * "1.234,56") → number, ou null quando não-numérico (cluster B-12 — mesma
 * normalização do export do operacoes/DataTable).
 */
export function ToNumericValue(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    const cleaned = String(raw)
        .replace(/^R\$\s*/, '')
        .replace(/%$/, '')
        .replace(/d$/, '') // "30d" da coverage
        .replace(/\./g, '')
        .replace(',', '.')
        .trim();
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}
